import WeightInfo, { TotalWeight } from '../../components/WeightInfo';
import { useAuth } from '../../context/AuthContext';
import { customerHomePath } from '../../lib/customerHome';
import { STORE_PAYMENT_OPTIONS, acceptedStorePayments } from '../../lib/storePayments';
import { useCart } from '../../context/CartContext';
import useDeliveryQuote from '../../lib/useDeliveryQuote';
import { cartLineKey } from '../../lib/cartLineKey';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowLeft, ArrowRight, Bike, Store } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { get, put, del } from '../../lib/api';

const Cart = () => {
  const { user } = useAuth();
  const { ready, items: syncedItems } = useCart();
  const [cartItems, setCartItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [orderTypeChoice, setOrderTypeChoice] = useState(null);
  const [showOrderChoice, setShowOrderChoice] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return;
    const controller = new AbortController();
    const fetchCartItems = async () => {
      try {
        setLoading(true);
        setError('');
        const [response, profileResponse] = await Promise.all([
          get('/cart', { signal: controller.signal }),
          user ? get('/customers/me', { signal: controller.signal }) : Promise.resolve({}),
        ]);
        const addresses = profileResponse.customer?.addresses || [];
        setDeliveryAddress(addresses.find((item) => item.isDefault) || addresses[0] || null);
        if (response.success && Array.isArray(response.cart)) {
          setCartItems(response.cart);
        } else {
          setError(response.message || 'Failed to fetch cart items.');
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch cart:', err);
        setError(err.response?.data?.message || 'An error occurred while fetching your cart.');
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
    return () => controller.abort();
  }, [ready, user]);

  useEffect(() => { setCartItems(syncedItems); }, [syncedItems]);

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      await handleRemoveItem(productId);
      return;
    }
    try {
      setError('');
      const response = await put(`/cart/${productId}`, { quantity: newQuantity });
      if (response.success && Array.isArray(response.cart)) {
        setCartItems(response.cart);
        setSelectedIds((ids) => ids.filter((id) => response.cart.some((item) => (item.cartId || cartLineKey(item)) === id)));
      } else {
        setError(response.message || 'Failed to update item quantity.');
      }
    } catch (err) {
      console.error('Failed to update quantity:', err);
      setError(err.response?.data?.message || 'An error occurred while updating the item quantity.');
    }
  };

  const handleRemoveItem = async (productId) => {
    try {
      setError('');
      const response = await del(`/cart/${productId}`);
      if (response.success && Array.isArray(response.cart)) {
        setCartItems(response.cart);
        setSelectedIds((ids) => ids.filter((id) => id !== String(productId)));
      } else {
        setError(response.message || 'Failed to remove item from cart.');
      }
    } catch (err) {
      console.error('Failed to remove item:', err);
      setError(err.response?.data?.message || 'An error occurred while removing the item.');
    }
  };

  const handleSelectItem = (cartId, checked) => {
    const selected = cartItems.find((item) => (item.cartId || cartLineKey(item)) === String(cartId));
    if (checked && (!selected || selected.available === false)) return;
    const storeId = item => String(item.vendorId?._id || item.vendorId);
    setSelectedIds((ids) => checked ? [...new Set([
      ...ids.filter(id => cartItems.some(item => (item.cartId || cartLineKey(item)) === id && storeId(item) === storeId(selected))), String(cartId),
    ])] : ids.filter((id) => id !== String(cartId)));
  };
  const handleSelectStore = (items, checked) => {
    const ids = items.filter((item) => item.available !== false).map((item) => item.cartId || cartLineKey(item));
    setSelectedIds((selected) => checked
      ? ids
      : selected.filter((id) => !ids.includes(id)));
  };
  useEffect(() => {
    setSelectedIds((ids) => ids.filter((id) => cartItems.some((item) => (item.cartId || cartLineKey(item)) === id && item.available !== false)));
  }, [cartItems]);
  const selectedItems = cartItems.filter((item) => item.available !== false && selectedIds.includes(item.cartId || cartLineKey(item)));
  const selectedSubtotal = selectedItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  const selectedItemCount = selectedItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const storeGroups = React.useMemo(() => {
    const groups = new Map();
    cartItems.forEach((item) => {
      const key = String(item.vendorId?._id || item.vendorId || item.vendorName || 'store');
      if (!groups.has(key)) groups.set(key, { id: key, name: item.vendorName || item.vendorId?.storeName || 'Store', logo: item.vendorLogo || item.vendorId?.logo, items: [] });
      groups.get(key).items.push(item);
    });
    return [...groups.values()];
  }, [cartItems]);
  const { deliveryFee, quoteLoading, quoteError } = useDeliveryQuote(selectedItems, deliveryAddress?._id, orderTypeChoice === 'DELIVERY');
  const total = selectedSubtotal + deliveryFee;

  const handleCheckout = () => {
    if (!selectedItems.length || selectedSubtotal <= 0 || selectedItems.some((item) => item.available === false)) return;
    setShowOrderChoice(true);
  };

  const continueToCheckout = (choice) => {
    setOrderTypeChoice(choice);
    setShowOrderChoice(false);
    navigate('/checkout', { state: { selectedIds: selectedItems.map((item) => item.cartId || cartLineKey(item)), orderType: choice } });
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading || !ready) {
    return <div className="text-center py-8">Loading cart...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-3">
      <header className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" aria-label="Go back" className="shrink-0" onClick={() => navigate(customerHomePath !== '/' ? customerHomePath : -1)}>
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Button>
        <h1 className="text-lg font-semibold">Your Cart</h1>
      </header>
      <p className="text-sm text-gray-500">Check out one store at a time. Selecting another store switches your selection and keeps the other items in your cart.</p>

      {cartItems.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-2">
            {storeGroups.map((group) => {
              const eligibleItems = group.items.filter((item) => item.available !== false);
              const selectedCount = eligibleItems.filter((item) => selectedIds.includes(item.cartId || cartLineKey(item))).length;
              const allSelected = eligibleItems.length > 0 && selectedCount === eligibleItems.length;
              const partiallySelected = selectedCount > 0 && !allSelected;
              return (
              <Card key={group.id}>
                <CardContent className="p-3">
                  <h2 aria-label={group.name} className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <input
                      type="checkbox"
                      aria-label={`Select all items from ${group.name}`}
                      aria-checked={partiallySelected ? 'mixed' : allSelected}
                      ref={(input) => { if (input) input.indeterminate = partiallySelected; }}
                      checked={allSelected}
                      disabled={!eligibleItems.length}
                      onChange={(event) => handleSelectStore(group.items, event.target.checked)}
                      className="h-5 w-5 shrink-0"
                    />
                    {group.logo ? <img src={group.logo} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" /> : <Store size={20} className="shrink-0 text-primary" aria-hidden="true" />}
                    <span className="break-words">{group.name}</span>
                  </h2>
                  <p className="mb-3 text-xs text-gray-500">Payments accepted: {STORE_PAYMENT_OPTIONS.filter(option => acceptedStorePayments(group.items[0]?.store?.acceptedPaymentMethods).includes(option.value)).map(option => option.label).join(', ')}</p>
                  <div className="divide-y">
            {group.items.map((item) => {
              const productId = item.cartId || cartLineKey(item);
              const name = item.name || 'Unnamed Product';
              const price = Number(item.price || 0);
              const quantity = Number(item.quantity || 0);
              const itemTotal = price * quantity;
              const isSelected = selectedIds.includes(productId);


              return (
                <div key={productId}>
                  <div className="flex flex-col gap-2 py-2 sm:flex-row">
                    {/* Selection */}
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        aria-label={`Select ${name}`}
                        disabled={item.available === false}
                        checked={isSelected}
                        onChange={(e) => handleSelectItem(productId, e.target.checked)}
                        className="sm:mt-1 w-5 h-5"
                      />
                    </div>

                    {/* Product Image */}
                    <div className="w-full sm:w-20 h-40 sm:h-20 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <span className="text-sm text-gray-400">No Image</span>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-grow">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <h3 className="font-bold">{name}</h3>
                          <WeightInfo {...item} />
                          {item.available === false && <p className="text-sm text-red-600">{item.unavailableReason}</p>}
                        </div>
                        <button
                          type="button"
                          aria-label={`Remove ${name}`}
                          className="text-gray-400 hover:text-destructive transition-colors"
                          onClick={() => handleRemoveItem(productId)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <p className="text-sm text-gray-500 mt-1 mb-2">₱{price.toFixed(2)} × {quantity}</p>

                      {/* Item subtotal and quantity */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="shrink-0 font-bold">₱{itemTotal.toFixed(2)}</span>
                        <div className="flex shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => handleUpdateQuantity(productId, quantity - 1)}
                          aria-label={`Decrease quantity of ${name}`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-sm font-medium min-w-5 text-center">{quantity}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => handleUpdateQuantity(productId, quantity + 1)}
                          aria-label={`Increase quantity of ${name}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="space-y-3">
            <Card>
              <CardContent className="p-3 space-y-2">
                <h2 className="text-xl font-bold">Order Summary</h2>
                <TotalWeight items={selectedItems} />

                <div className="flex justify-between text-sm text-gray-600">
                  <span>Selected subtotal ({selectedItemCount} items)</span>
                  <span>₱{selectedSubtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-sm text-gray-600">
                  <span>Delivery Fee</span>
                  <span>{quoteLoading ? 'Calculating...' : quoteError ? 'Unavailable' : '\u20B1' + deliveryFee.toFixed(2)}</span>
                </div>

                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-primary">₱{total.toFixed(2)}</span>
                </div>

                <Button
                  type="button"
                  className="w-full gap-2 mt-0 py-3 text-base"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={!selectedItems.length || selectedSubtotal <= 0}
                >
                  Go to Checkout
                  <ArrowRight className="w-4 h-4" />
                </Button>

                {!selectedItems.length && (
                  <p className="text-center text-sm text-gray-500">
                    Please select items to continue.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <Card className="p-6 text-center space-y-2">
          <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto flex items-center justify-center">
            <Trash2 className="w-8 h-8 text-gray-300" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Your cart is empty</h2>
            <p className="text-gray-500">Looks like you haven't added anything yet.</p>
          </div>
          <Link to={customerHomePath}>
            <Button variant="outline">Browse Restaurants</Button>
          </Link>
        </Card>
      )}
      {selectedItems.length > 0 && showOrderChoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-labelledby="order-type-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl">
            <div className="mb-3"><p className="text-sm font-semibold text-primary">Almost there</p><h2 id="order-type-title" className="mt-1 text-2xl font-bold">How would you like to receive it?</h2><p className="mt-2 text-sm text-gray-500">Choose now so we can calculate your total before checkout.</p></div>
            <div className="grid gap-2 sm:grid-cols-2"><button type="button" className="rounded-xl border p-3 text-left transition hover:border-primary hover:bg-primary/5" onClick={() => continueToCheckout('DELIVERY')}><Bike className="mb-3 h-6 w-6 text-primary" /><span className="block font-semibold">Delivery</span><span className="mt-1 block text-xs text-gray-500">We will calculate the delivery fee.</span></button><button type="button" className="rounded-xl border p-3 text-left transition hover:border-primary hover:bg-primary/5" onClick={() => continueToCheckout('PICKUP')}><Store className="mb-3 h-6 w-6 text-primary" /><span className="block font-semibold">Pick up</span><span className="mt-1 block text-xs text-gray-500">No delivery fee. Choose an accepted payment at checkout.</span></button></div><button type="button" className="mt-3 w-full text-sm text-gray-500 hover:text-gray-900" onClick={() => setShowOrderChoice(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
